import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Upload, Sparkles } from "lucide-react";
import { fetchProducts, createProduct, updateProduct, deleteProduct, uploadProductImage, absoluteUrl } from "@/lib/api";
import { formatApiErrorDetail } from "@/contexts/AuthContext";

const empty = { name: "", category: "", description: "", image_url: "", featured: false };

export default function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = none, {} = create, {id,...} = edit

  const refresh = async () => {
    setLoading(true);
    try { setItems(await fetchProducts()); } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteProduct(id);
      toast.success("Product deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleFeatured = async (p) => {
    try {
      await updateProduct(p.id, { featured: !p.featured });
      toast.success(!p.featured ? "Marked as featured" : "Removed from featured");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  const featuredCount = items.filter((p) => p.featured).length;

  return (
    <div data-testid="admin-products">
      <Header
        title="Products"
        subtitle={`Manage your medicine & supply catalog shown on the public site. ${featuredCount}/4 featured slots used.`}
        onAdd={() => setEditing(empty)}
        addLabel="Add Product"
        testidAdd="products-add"
      />

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => <div key={i} className="h-44 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-slate-500 py-10 text-center">No products yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p) => (
            <article key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden group">
              <div className="aspect-[4/3] bg-slate-100 relative">
                {p.image_url && <img src={absoluteUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />}
                <span className="absolute top-3 left-3 bg-white/90 text-blue-700 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">{p.category}</span>
                {p.featured && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full shadow">
                    <Sparkles size={10} /> Featured
                  </span>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-heading font-semibold text-slate-900 truncate">{p.name}</h3>
                <p className="text-sm text-slate-500 line-clamp-2 mt-1">{p.description}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    data-testid={`product-toggle-featured-${p.id}`}
                    onClick={() => toggleFeatured(p)}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                      p.featured
                        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "text-slate-600 hover:text-emerald-600 hover:bg-emerald-50"
                    }`}
                  >
                    <Sparkles size={13} /> {p.featured ? "Featured" : "Feature"}
                  </button>
                  <button data-testid={`product-edit-${p.id}`} onClick={() => setEditing(p)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50">
                    <Pencil size={13} /> Edit
                  </button>
                  <button data-testid={`product-delete-${p.id}`} onClick={() => handleDelete(p.id)} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && <ProductDialog initial={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function Header({ title, subtitle, onAdd, addLabel, testidAdd }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-7">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900">{title}</h1>
        <p className="text-slate-500 mt-1 text-sm">{subtitle}</p>
      </div>
      <button onClick={onAdd} data-testid={testidAdd} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-medium shadow-md shadow-blue-600/20 shrink-0">
        <Plus size={16} /> {addLabel}
      </button>
    </div>
  );
}

function ProductDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...empty, ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!initial.id;
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadProductImage(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error("Please upload a product image");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateProduct(initial.id, form);
        toast.success("Product updated");
      } else {
        await createProduct(form);
        toast.success("Product created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-testid="product-dialog">
      <form onSubmit={onSave} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="font-heading text-xl font-semibold text-slate-900">{isEdit ? "Edit Product" : "Add Product"}</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <FormField label="Name *" name="name" value={form.name} onChange={onChange} required testid="product-input-name" />
          <FormField label="Category *" name="category" value={form.category} onChange={onChange} required testid="product-input-category" placeholder="e.g. Antibiotic" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">Description *</label>
            <textarea name="description" value={form.description} onChange={onChange} required rows={3} data-testid="product-input-description" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          {/* Image Upload */}
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">Product Image *</label>
            <div className="flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="product-input-image-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-2.5 text-sm hover:bg-slate-50 disabled:opacity-70">
                {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> {form.image_url ? "Replace image" : "Choose image"}</>}
              </button>
              {form.image_url && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="text-xs text-red-500 hover:underline">Remove</button>
              )}
            </div>
            {form.image_url && (
              <div className="mt-3 rounded-xl border border-slate-100 overflow-hidden bg-slate-50 max-h-48">
                <img src={absoluteUrl(form.image_url)} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          {/* Featured toggle */}
          <label className="flex items-start gap-3 p-4 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
            <input
              type="checkbox"
              checked={!!form.featured}
              onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
              data-testid="product-input-featured"
              className="mt-1 w-4 h-4 accent-emerald-600 cursor-pointer"
            />
            <div>
              <div className="flex items-center gap-1.5 font-medium text-sm text-slate-900">
                <Sparkles size={14} className="text-emerald-600" /> Show in Featured Today
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Spotlights this product at the top of the homepage. Max 4 visible at a time.</p>
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-slate-100 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving} data-testid="product-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-sm font-medium rounded-lg disabled:opacity-70">
            {saving && <Loader2 size={14} className="animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, name, value, onChange, required, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5 block">{label}</label>
      <input type={type} name={name} value={value || ""} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}
        className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500" />
    </div>
  );
}
